import { VOID } from 'ol/functions.js';

let withCredentials = false;
let geoJsonWorkerBlobUrl = null;

function getGeoJsonWorker() {
    if (typeof Worker === 'undefined') {
        return null;
    }

    if (geoJsonWorkerBlobUrl == null) {
        const workerScript =
            "self.onmessage = async function(event) {\n" +
            " var req = event.data || {};\n" +
            "  var batchSize = req.batchSize > 0 ? req.batchSize : 600;\n" +
            "  try {\n" +
            "    var response = await fetch(req.url, { method: 'GET', credentials: req.withCredentials ? 'include' : 'same-origin' });\n" +
            "    if (!response.ok) {\n" +
            "      self.postMessage({ type: 'error', status: response.status, message: 'request_failed' });\n" +
            "      return;\n" +
            "    }\n" +
            "    var text = await response.text();\n" +
            "    if (typeof text === 'string' && text.indexOf('<?xml ') === 0) {\n" +
            "      self.postMessage({ type: 'service_exception', text: text });\n" +
            "      return;\n" +
            "    }\n" +
            "    var parsed = JSON.parse(text);\n" +
            "    if (!parsed || !Array.isArray(parsed.features)) {\n" +
            "      self.postMessage({ type: 'single', collection: parsed });\n" +
            "      self.postMessage({ type: 'done' });\n" +
            "      return;\n" +
            "    }\n" +
            "    var baseCollection = {};\n" +
            "    for (var key in parsed) {\n" +
            "      if (Object.prototype.hasOwnProperty.call(parsed, key) && key !== 'features') {\n" +
            "        baseCollection[key] = parsed[key];\n" +
            "      }\n" +
            "    }\n" +
            "    var allFeatures = parsed.features;\n" +
            "    for (var i = 0; i < allFeatures.length; i += batchSize) {\n" +
            "      var batch = allFeatures.slice(i, i + batchSize);\n" +
            "      self.postMessage({ type: 'batch', collection: Object.assign({}, baseCollection, { features: batch }) });\n" +
            "    }\n" +
            "    self.postMessage({ type: 'done' });\n" +
            "  } catch (error) {\n" +
            "    self.postMessage({ type: 'error', status: 0, message: error && error.message ? error.message : 'worker_failed' });\n" +
            "  }\n" +
            "};\n";
        geoJsonWorkerBlobUrl = URL.createObjectURL(new Blob([workerScript], { type: 'application/javascript' }));
    }

    try {
        return new Worker(geoJsonWorkerBlobUrl);
    } catch (error) {
        return null;
    }
}

function loadFeaturesWithXhr(
    url,
    format,
    extent,
    resolution,
    projection,
    success,
    failure
) {
    const xhr = new XMLHttpRequest();
    xhr.open(
        'GET',
        typeof url === 'function' ? url(extent, resolution, projection) : url,
        true
    );
    if (format.getType() == 'arraybuffer') {
        xhr.responseType = 'arraybuffer';
    } else if (format.getType() == 'json') {
        xhr.responseType = 'json';
    }
    xhr.withCredentials = withCredentials;

    xhr.onload = function () {
        if (!xhr.status || (xhr.status >= 200 && xhr.status < 300)) {
            const type = format.getType();
            let payload;
            if (type == 'json' || type == 'text') {
                if (type == 'json' && xhr.response != null) {
                    payload = xhr.response;
                } else if (
                    xhr.responseText.startsWith('<?xml ') &&
                    xhr.responseXML != null &&
                    xhr.responseXML.documentElement != null &&
                    xhr.responseXML.documentElement.nodeName == 'ServiceExceptionReport'
                ) {
                    toastr.error(xhr.responseXML.documentElement.textContent);
                    return;
                } else {
                    payload = xhr.responseText;
                }
            } else if (type == 'xml') {
                payload = xhr.responseXML;
                if (!payload) {
                    payload = new DOMParser().parseFromString(
                        xhr.responseText,
                        'application/xml'
                    );
                }
            } else if (type == 'arraybuffer') {
                payload = xhr.response;
            }
            if (payload) {
                const features = format.readFeatures(payload, {
                    extent: extent,
                    featureProjection: projection,
                });
                success(features, null);
            } else {
                failure();
            }
        } else {
            failure();
        }
    };
    xhr.onerror = failure;
    xhr.send();
}

function loadFeaturesWithGeoJsonWorker(
    url,
    format,
    extent,
    resolution,
    projection,
    sourceObj,
    success,
    failure
) {
    const requestUrl = typeof url === 'function' ? url(extent, resolution, projection) : url;
    const worker = getGeoJsonWorker();
    if (worker == null) {
        loadFeaturesWithXhr(url, format, extent, resolution, projection, success, failure);
        return;
    }

    let failed = false;
    let done = false;
    let processing = false;
    const queue = [];
    const allFeatures = [];

    function failAndFallback() {
        if (failed) {
            return;
        }
        failed = true;
        try {
            worker.terminate();
        } catch (error) {
            // nothing to do
        }
        loadFeaturesWithXhr(
            url,
            format,
            extent,
            resolution,
            projection,
            function (features) {
                sourceObj.clear(true);
                sourceObj.addFeatures(features);
                success(features, null);
            },
            failure
        );
    }

    function processNext() {
        if (failed || processing) {
            return;
        }

        if (queue.length === 0) {
            if (done) {
                try {
                    worker.terminate();
                } catch (error) {
                    // nothing to do
                }
                success(allFeatures, null);
            }
            return;
        }

        processing = true;
        const collection = queue.shift();
        requestAnimationFrame(function () {
            try {
                const features = format.readFeatures(collection, {
                    extent: extent,
                    featureProjection: projection,
                });
                if (Array.isArray(features) && features.length > 0) {
                    sourceObj.addFeatures(features);
                    Array.prototype.push.apply(allFeatures, features);
                }
            } catch (error) {
                failAndFallback();
                return;
            }

            processing = false;
            processNext();
        });
    }

    sourceObj.clear(true);

    worker.onmessage = function (event) {
        if (failed) {
            return;
        }

        const data = event.data || {};
        if (data.type === 'batch' || data.type === 'single') {
            queue.push(data.collection);
            processNext();
            return;
        }

        if (data.type === 'service_exception') {
            if (typeof data.text === 'string') {
                toastr.error(data.text);
            }
            try {
                worker.terminate();
            } catch (error) {
                // nothing to do
            }
            failure();
            return;
        }

        if (data.type === 'done') {
            done = true;
            processNext();
            return;
        }

        failAndFallback();
    };

    worker.onerror = failAndFallback;
    worker.postMessage({
        url: requestUrl,
        withCredentials: withCredentials === true,
        batchSize: Number.isFinite(format.workerBatchSize)
            ? format.workerBatchSize
            : 600,
    });
}

export function loadFeaturesXhr(
    url,
    format,
    extent,
    resolution,
    projection,
    success,
    failure
) {
    loadFeaturesWithXhr(
        url,
        format,
        extent,
        resolution,
        projection,
        success,
        failure
    );
}

export function xhr(url, format) {
    return function (extent, resolution, projection, success, failure) {
        const source = this;

        if (
            format != null &&
            format.getType() == 'json' &&
            format.useWorkerGeoJson === true
        ) {
            loadFeaturesWithGeoJsonWorker(
                url,
                format,
                extent,
                resolution,
                projection,
                source,
                function (features) {
                    if (success !== undefined) {
                        success(features);
                    }
                },
                failure ? failure : VOID
            );
            return;
        }

        loadFeaturesWithXhr(
            url,
            format,
            extent,
            resolution,
            projection,
            function (features) {
                source.clear(true);
                source.addFeatures(features);
                if (success !== undefined) {
                    success(features);
                }
            },
            failure ? failure : VOID
        );
    };
}

export function setWithCredentials(xhrWithCredentials) {
    withCredentials = xhrWithCredentials;
}
